import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const containerStyle = { maxWidth: 1400, margin: '40px auto', padding: 24, border: '1px solid #ddd', borderRadius: 10, background: '#fafafa' };
const inputStyle = { width: '100%', padding: 10, boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: 6, fontSize: 14 };
const labelStyle = { display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 14 };
const btnPrimary = { padding: '10px 16px', background: '#28a745', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', marginRight: 8 };
const btnSecondary = { padding: '10px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', marginRight: 8 };
const btnDanger = { padding: '10px 16px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' };
const btnEdit = { padding: '6px 12px', background: '#ffc107', color: '#000', border: 'none', borderRadius: 6, cursor: 'pointer', marginRight: 4 };
const btnReset = { padding: '6px 12px', background: '#17a2b8', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', marginRight: 4 };

// ✅ UPDATED: Added new fields to the empty form state
const emptyForm = { 
  student_id: '', first_name: '', last_name: '', username: '', password: '', 
  gender: 'M', date_of_birth: '', school_name: '', age: '', current_grade: '' 
};

const calculateAge = (dob) => {
  if (!dob) return '—';
  const birthYear = new Date(dob).getFullYear();
  const currentYear = new Date().getFullYear();
  return currentYear - birthYear >= 0 ? currentYear - birthYear : '—';
};

const API_BASE = process.env.REACT_APP_API_BASE || '/api';

export default function ManageStudent() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => { fetchStudents(); }, []);

  const fetchStudents = async () => {
    setLoading(true); setError('');
    try {
      // Use the standard API_BASE environment variable for better portability
      const res = await fetch(`${API_BASE}/admin/students`);
      if (!res.ok) throw new Error('Failed to fetch students');
      setStudents(await res.json());
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  // ✅ UPDATED: Populate new fields when clicking Edit
  const handleEdit = (s) => {
    setEditingId(s.id);
    setFormData({ 
      student_id: s.student_id, first_name: s.first_name || '', last_name: s.last_name || '',
      username: s.username, password: '', gender: s.gender || 'M', 
      date_of_birth: s.date_of_birth ? s.date_of_birth.split('T')[0] : '',
      school_name: s.school_name || '',
      age: s.age || '',
      current_grade: s.current_grade || ''
    });
  };

  const handleCancel = () => { setEditingId(null); setFormData(emptyForm); };

  const handleSave = async () => {
    setError('');
    try {
      const res = await fetch(`${API_BASE}/admin/students/${editingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Update failed');
      await fetchStudents();
      handleCancel();
    } catch (err) { setError(err.message); }
  };

  const handleResetPassword = async (id, username) => {
    const newPass = window.prompt(`Enter new password for student "${username}":`);
    if (!newPass || newPass.trim() === '') return; 
    if (newPass.trim().length < 6) {
      alert('❌ Password must be at least 6 characters.');
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/admin/students/${id}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: newPass.trim() })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to reset password');
      alert('✅ Password reset successfully!');
    } catch (err) { alert(`❌ ${err.message}`); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this student? This cannot be undone.')) return;
    try {
      const res = await fetch(`${API_BASE}/admin/students/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      setStudents(prev => prev.filter(s => s.id !== id));
    } catch (err) { setError(err.message); }
  };

  const filteredStudents = students.filter(s => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return true;
    return (
      (s.student_id || '').toLowerCase().includes(q) ||
      (s.first_name || '').toLowerCase().includes(q) ||
      (s.last_name || '').toLowerCase().includes(q) ||
      (s.username || '').toLowerCase().includes(q) ||
      (s.school_name || '').toLowerCase().includes(q)
    );
  });

  const formatDOB = (d) => d ? d.split('T')[0] : '—';
  const formatCreated = (d) => d ? new Date(d).toLocaleDateString() : '—';

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>Manage Students</h2>
        <button onClick={() => navigate('/admin/dashboard')} style={btnSecondary}>← Back</button>
      </div>

      {error && <div style={{ padding: 10, background: '#f8d7da', color: '#721c24', borderRadius: 6, marginBottom: 15 }}>{error}</div>}

      <div style={{ marginBottom: 20, display: 'flex', gap: 10 }}>
        <input type="text" placeholder="🔍 Search by Student ID, Name, Username, or School..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} style={{ ...inputStyle, marginBottom: 0, flex: 1 }} />
        {searchQuery && <button onClick={() => setSearchQuery('')} style={btnSecondary}>Clear</button>}
      </div>

      {/* ✅ UPDATED: Edit Form includes School, Age, and Grade */}
      {editingId && (
        <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd', marginBottom: 20 }}>
          <h3 style={{ marginTop: 0 }}>Edit Student</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 15 }}>
            <label style={labelStyle}>Student ID: <input style={inputStyle} name="student_id" value={formData.student_id} readOnly disabled /></label>
            <label style={labelStyle}>Username: <input style={inputStyle} name="username" value={formData.username} onChange={handleChange} /></label>
            <label style={labelStyle}>New Password: <input style={inputStyle} type="password" name="password" value={formData.password} onChange={handleChange} placeholder="Leave blank to keep current" /></label>
            
            <label style={labelStyle}>First Name: <input style={inputStyle} name="first_name" value={formData.first_name} onChange={handleChange} /></label>
            <label style={labelStyle}>Last Name: <input style={inputStyle} name="last_name" value={formData.last_name} onChange={handleChange} /></label>
            <label style={labelStyle}>Gender: <select style={inputStyle} name="gender" value={formData.gender} onChange={handleChange}><option value="M">Male</option><option value="F">Female</option></select></label>
            
            <label style={labelStyle}>Date of Birth: <input style={inputStyle} type="date" name="date_of_birth" value={formData.date_of_birth} onChange={handleChange} /></label>
            <label style={labelStyle}>Age: <input style={inputStyle} type="number" name="age" value={formData.age} onChange={handleChange} placeholder="e.g. 10" /></label>
            <label style={labelStyle}>Current Grade: <input style={inputStyle} name="current_grade" value={formData.current_grade} onChange={handleChange} placeholder="e.g. Grade 5" /></label>
            
            <label style={{...labelStyle, gridColumn: 'span 3'}}>Name of School: <input style={inputStyle} name="school_name" value={formData.school_name} onChange={handleChange} placeholder="e.g. Springfield Elementary" /></label>
          </div>
          <div style={{ marginTop: 15 }}>
            <button onClick={handleSave} style={btnPrimary}>Save Changes</button>
            <button onClick={handleCancel} style={btnSecondary}>Cancel</button>
          </div>
        </div>
      )}

      {/* ✅ Added overflowX: 'auto' for horizontal scrolling on smaller screens */}
      <div style={{ overflowX: 'auto' }}>
        {/* ✅ UPDATED: Table Header includes School and Grade */}
        <div style={{ display: 'grid', gridTemplateColumns: '0.8fr 1fr 1fr 1.2fr 0.8fr 0.6fr 1.5fr 0.8fr 1fr 1.2fr 2.5fr', gap: 10, padding: '10px 15px', background: '#e9ecef', borderRadius: '8px 8px 0 0', fontWeight: 600, fontSize: 13, minWidth: '1100px' }}>
          <div>ID</div><div>First Name</div><div>Last Name</div><div>Username</div>
          <div>Gender</div><div>Age</div><div>School</div><div>Grade</div><div>DOB</div><div>Created</div><div>Actions</div>
        </div>

        {loading && <p style={{ textAlign: 'center', padding: 20 }}>Loading students...</p>}
        {!loading && filteredStudents.length === 0 && (
          <p style={{ textAlign: 'center', padding: 20, color: '#666', minWidth: '1100px' }}>{searchQuery ? 'No matching students found.' : 'No students found.'}</p>
        )}

        {/* ✅ UPDATED: Table Rows include School and Grade */}
        {!loading && filteredStudents.map(s => (
          <div key={s.id} style={{ display: 'grid', gridTemplateColumns: '0.8fr 1fr 1fr 1.2fr 0.8fr 0.6fr 1.5fr 0.8fr 1fr 1.2fr 2.5fr', gap: 10, padding: '12px 15px', background: '#fff', borderBottom: '1px solid #eee', alignItems: 'center', fontSize: 13, minWidth: '1100px' }}>
            <div style={{ fontFamily: 'monospace', fontWeight: 500 }}>{s.student_id}</div>
            <div>{s.first_name || '—'}</div>
            <div>{s.last_name || '—'}</div>
            <div>{s.username}</div>
            <div>{s.gender === 'M' ? 'Male' : 'Female'}</div>
            {/* Fallback to calculated age if DB age is missing */}
            <div style={{ fontWeight: 500, color: '#333' }}>{s.age || calculateAge(s.date_of_birth)}</div>
            <div style={{ color: '#555' }}>{s.school_name || '—'}</div>
            <div>{s.current_grade || '—'}</div>
            <div>{formatDOB(s.date_of_birth)}</div>
            <div style={{ color: '#888' }}>{formatCreated(s.created_at)}</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button onClick={() => handleEdit(s)} style={btnEdit}>Edit</button>
              <button onClick={() => handleResetPassword(s.id, s.username)} style={btnReset}>Reset Pwd</button>
              <button onClick={() => handleDelete(s.id)} style={{ ...btnDanger, padding: '6px 12px' }}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}